import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { DownloadIcon } from '@radix-ui/react-icons'

import {
  DropdownContent,
  DropdownItem,
  DropdownRoot,
  DropdownTrigger
} from 'Components/DropdownMenu/DropdownMenu'

import { useDownloadBenefitRecipientsOverviewReport } from 'Hooks/api/JOONAdmin/useDownloadBenefitRecipientsOverviewReport'

import { spacingLG, spacingXXS } from 'Utils/styles/spacing'
import { useJoonAdminContext } from 'Contexts/JOONAdmin/JOONAdminContext'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { useDownloadPurchasesReport } from 'Hooks/api/JOONAdmin/useDownloadPurchasesReport'
import { BaseText } from 'Utils/styles/text'
import { elfGreen, elfGreenActive, elfGreenHover, white } from 'Utils/styles/colors'
import { useDateSelectionModal } from 'Hooks/modals/useDateSelectionModal'
import { useDownloadInvoicesReport } from 'Hooks/api/JOONAdmin/useDownloadInvoicesReport'
import { useConsolidatedPurchasesReportModal } from 'Hooks/modals/useConsolidatedPurchasesReportModal'
import { useGenerateConsolidatedPurchasesReport } from 'Hooks/api/JOONAdmin/useGenerateConsolidatedPurchasesReport'
import { useDownloadCompletePurchasesReport } from 'Hooks/api/JOONAdmin/useDownloadCompletePurchasesReport'
import { useGeneratePurchaseReportModal } from 'Hooks/modals/useGeneratePurchaseReportModal'
import { useGeneratePurchaseReport } from 'Hooks/api/JOONAdmin/useGeneratePurchaseReport'
import { useDownloadEmployerReceipts } from 'Hooks/api/employer/useDownloadEmployerProgramReceipts'
import { useSelectModal } from 'Hooks/modals/useSelectModal'
import { useCompletePurchasesReportModal } from 'Hooks/modals/useCompletePurchasesReportModal'
import {
  getEmployerReceiptDownloadOptions,
  parseBenefitProgramDownloadOption
} from 'Utils/helpers/receipts'

const Container = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacingXXS};
`

const Trigger = styled.div`
  ${BaseText};
  position: relative;
  height: 48px;
  padding: 0 ${spacingLG};

  border-radius: 8px;

  cursor: pointer;
  background-color: ${elfGreen};
  color: ${white};
  &:hover,
  &:focus {
    outline: none;
    background-color: ${elfGreenHover};
  }
  &:active {
    background-color: ${elfGreenActive};
  }

  display: flex;
  justify-content: center;
  align-items: center;
`

export const EmployerReportsMenuButton: FC = () => {
  const {
    state: { selectedEmployer }
  } = useJoonAdminContext()

  const {
    state: { employerInfo }
  } = useEmployerInfoContext()

  const { showModal } = useDateSelectionModal()
  const { showModal: showConsolidatedModal } = useConsolidatedPurchasesReportModal()
  const { showModal: showMonthSelector } = useSelectModal()
  const { showModal: showCompletePurchasesReportModal } = useCompletePurchasesReportModal()
  const { showModal: showGeneratePurchaseReportModal } = useGeneratePurchaseReportModal()

  const downloadBenefitsRecipientsOverviewReport = useDownloadBenefitRecipientsOverviewReport()
  const downloadPurchasesReport = useDownloadPurchasesReport()
  const downloadCompletePurchasesReport = useDownloadCompletePurchasesReport()
  const downloadInvoicesReport = useDownloadInvoicesReport()
  const generateConsolidatedPurchasesReport = useGenerateConsolidatedPurchasesReport()
  const generatePurchaseReport = useGeneratePurchaseReport()
  const { mutate: mutateEmployerReceipts } = useDownloadEmployerReceipts()

  const receiptMonthOptions = useMemo(() => {
    if (!employerInfo) return []

    const { earliestInvoiceDate } = employerInfo
    return getEmployerReceiptDownloadOptions(earliestInvoiceDate || new Date())
  }, [employerInfo])

  const handleDownloadBenefitRecipientsOverview = useCallback(async () => {
    if (!selectedEmployer) return

    const selectedDate = await showModal('Benefits Recipients Overview - Select Month', '')

    if (!selectedDate) return

    const day = selectedDate.getDate()
    const month = selectedDate.getUTCMonth() + 1
    const year = selectedDate.getUTCFullYear()

    downloadBenefitsRecipientsOverviewReport.mutate({
      employerId: selectedEmployer.id,
      day,
      month,
      year
    })
  }, [selectedEmployer, showModal, downloadBenefitsRecipientsOverviewReport])

  const handleDownloadPurchasesReport = useCallback(async () => {
    if (!selectedEmployer) return

    const selectedDate = await showModal('Purchase Report - Select Date', '')

    if (!selectedDate) return

    downloadPurchasesReport.mutate({
      employerId: selectedEmployer.id,
      employerName: selectedEmployer.name,
      reportDate: selectedDate
    })
  }, [selectedEmployer, showModal, downloadPurchasesReport])

  const handleDownloadCompletePurchasesReport = useCallback(async () => {
    if (!selectedEmployer) return

    const result = await showCompletePurchasesReportModal({
      countries: selectedEmployer.countries
    })

    if (!result) return

    downloadCompletePurchasesReport.mutate({
      employerId: selectedEmployer.id,
      employerName: selectedEmployer.name,
      reportDate: result.date,
      countryCode: result.countryCode
    })
  }, [selectedEmployer, showCompletePurchasesReportModal, downloadCompletePurchasesReport])

  const handleDownloadInvoicesReport = useCallback(async () => {
    if (!selectedEmployer) return

    const selectedDate = await showModal('Invoices Report - Select Month', '')

    if (!selectedDate) return

    downloadInvoicesReport.mutate({
      employerId: selectedEmployer.id,
      employerName: selectedEmployer.name,
      reportDate: selectedDate
    })
  }, [selectedEmployer, showModal, downloadInvoicesReport])

  const handleGenerateConsolidatedPurchasesReport = useCallback(async () => {
    if (!selectedEmployer) return

    const result = await showConsolidatedModal({
      countries: selectedEmployer.countries
    })

    if (!result) return

    generateConsolidatedPurchasesReport.mutate({
      employerId: selectedEmployer.id,
      startDate: result.startDate,
      endDate: result.endDate,
      reportName: result.reportName,
      countryCode: result.countryCode
    })
  }, [selectedEmployer, showConsolidatedModal, generateConsolidatedPurchasesReport])

  const handleGeneratePurchaseReport = useCallback(async () => {
    if (!selectedEmployer) return

    const result = await showGeneratePurchaseReportModal({
      employer: selectedEmployer
    })

    if (!result) return

    generatePurchaseReport.mutate({
      employerId: selectedEmployer.id,
      date: result.date,
      countryCode: result.countryCode
    })
  }, [selectedEmployer, showGeneratePurchaseReportModal, generatePurchaseReport])

  const handleDownloadEmployerReceipts = useCallback(async () => {
    if (!selectedEmployer) return

    const selectedMonth = await showMonthSelector({
      title: 'Download Employer Receipts',
      selectOptions: receiptMonthOptions,
      placeholderText: 'Select a month',
      confirmButtonId: 'download.label'
    })

    if (!selectedMonth) return

    const parsedDates = parseBenefitProgramDownloadOption(selectedMonth)
    mutateEmployerReceipts({
      employerId: selectedEmployer.id,
      ...parsedDates
    })
  }, [selectedEmployer, showMonthSelector, receiptMonthOptions, mutateEmployerReceipts])

  return (
    <Container>
      <DropdownRoot>
        <DropdownTrigger>
          <Trigger>Reports</Trigger>
        </DropdownTrigger>

        <DropdownContent>
          <DropdownItem onSelect={() => handleDownloadBenefitRecipientsOverview()}>
            <DownloadIcon />
            Benefit Recipients Overview
          </DropdownItem>
          <DropdownItem onSelect={() => handleDownloadPurchasesReport()}>
            <DownloadIcon />
            Raw Purchases Report Data
          </DropdownItem>
          <DropdownItem onSelect={() => handleDownloadCompletePurchasesReport()}>
            <DownloadIcon />
            Complete Purchases Report
          </DropdownItem>
          <DropdownItem onSelect={() => handleDownloadInvoicesReport()}>
            <DownloadIcon />
            Invoices Report
          </DropdownItem>
          <DropdownItem onSelect={() => handleDownloadEmployerReceipts()}>
            <DownloadIcon />
            Receipts (All Programs)
          </DropdownItem>
        </DropdownContent>
      </DropdownRoot>

      <DropdownRoot>
        <DropdownTrigger>
          <Trigger>Actions</Trigger>
        </DropdownTrigger>

        <DropdownContent>
          <DropdownItem onSelect={() => handleGeneratePurchaseReport()}>
            <DownloadIcon />
            Regenerate Purchase Report
          </DropdownItem>
          <DropdownItem onSelect={() => handleGenerateConsolidatedPurchasesReport()}>
            <DownloadIcon />
            Generate Consolidated Purchase Report
          </DropdownItem>
        </DropdownContent>
      </DropdownRoot>
    </Container>
  )
}
